import {FunnelContext, FunnelLayout, FunnelProvider, FunnelRoutes} from '@tinct/web-funnel';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FunnelDropdownOptions,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@tinct/web-components";
import {useJobStore} from "@tinct/web-layout";
import {BUYER_IMPORT_JOB_TAG} from "@tinct/web-feature-companies";
import {Step1CampaignConfiguration} from "../steps/step1-campaign-configuration/step1-campaign-configuration";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Step2Analyzing} from "../steps/step2-analyzing/step2-analyzing";
import {Step2bBuilding} from "../steps/step2b-building/step2b-building";
import {useCreateStep3Edit} from "../steps/step3-edit/hooks";
import {useCreateStep4Audience} from "../steps/step4-audience/hooks";
import {useCreateStep6Preview} from "../steps/step6-preview/hooks";
import {useAddConfirmedStep, useGetCampaignById} from "@tinct/core-api";
import {ArrowLeftIcon, ArrowRightIcon, EllipsisVertical, RefreshCcw, Zap} from "lucide-react";
import {ReadCampaign} from "@tinct/core-api-models";
import {useOpenPaywall} from '@tinct/web-feature-payment';
import {Step5SampleGeneration} from "../steps/step5-sample-generation/step5-sample-generation";
import {useCreateStep7Publish} from "../steps/step7-publish/hooks";
import {REMATCHING_LOGOS_REASON, useIsRematchingLogos} from "../../containers/landing-page-studio/hooks";
import {Step8Publishing} from "../steps/step8-publishing/step8-publishing";
import {CampaignCreationFunnelDispatcher} from "./campaign-creation-funnel-dispatcher";
import {getCampaignSetupStep} from "./get-campaign-setup-step";
import {StudioModeTabs} from "../../containers/landing-page-studio/components/studio-mode-tabs";


export type CampaignCreationFunnelState = {
  campaign: ReadCampaign;
};

// A step is reachable once the campaign's resume step is at or past it, with
// a one-step tolerance: each step's confirm navigates BEFORE the campaign
// refetch lands, so the immediate next step must stay reachable on a stale
// snapshot. This still blocks long jumps (e.g. Publish from Configure) in
// the stepper AND on hand-typed URLs (StepChecker redirects to dispatch).
// Positions come from the funnel's own steps array, NOT a shared static map:
// each origin's array is contiguous, so "next step" always means the step the
// user actually lands on next (a shared map put the new-origin-only 'edit'
// between 'analyze' and 'audience', which made 'audience' unreachable on a
// stale snapshot and looped analyze → dispatch → analyze forever).
const availableFrom =
  (stepId: string) =>
  ({ steps, state }: FunnelContext<CampaignCreationFunnelState>) => {
    const campaign = state?.campaign;
    if (!campaign) return true;
    // Campaigns that completed setup at least once (live, or unpublished
    // afterwards) navigate freely — the management surfaces deep-link into
    // arbitrary steps for them (settings cards, per-variant Edit, banners).
    if (campaign.status === 'published' || campaign.status === 'unpublished') {
      return true;
    }
    const current = getCampaignSetupStep(campaign);
    const currentIndex = steps.findIndex((step) => step.id === current);
    // 'live' (or any step this origin's funnel doesn't mount) means setup is
    // past every gate handled here — navigate freely.
    if (currentIndex === -1) return true;
    return currentIndex + 1 >= steps.findIndex((step) => step.id === stepId);
  };


export const CampaignCreationFunnel = () => {
  const { id } = useParams<{ id: string }>();
  const step6Preview = useCreateStep6Preview();
  const step3Edit = useCreateStep3Edit({campaignId: id || ''});
  const step4Audience = useCreateStep4Audience({campaignId: id || ''});
  const step7Publish = useCreateStep7Publish({campaignId: id || ''});
  const { mutateAsync: addConfirmedStep } = useAddConfirmedStep();
  const {openPaywall} = useOpenPaywall();
  const navigate = useNavigate();

  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();

  // Block advancing past the personalize & preview step while a customer-logo
  // (buyer) import is still running, so publishing never starts from a
  // half-imported pool.
  const isImportingBuyers = useJobStore((s) =>
    s.jobs.some((job) => job.tag === BUYER_IMPORT_JOB_TAG && job.status === 'running')
  );
  // Same while the logos of the generated variants are being re-matched to an
  // updated library: what the user would publish is still changing.
  const isRematchingLogos = useIsRematchingLogos();

  const { data: campaign, isLoading } = useGetCampaignById({
    id: id || '',
    fields: {
      id: null,
      name: null,
      status: null,
      landingPageOriginType: null,
      landingPageVariationAxis: null,
      landingPage: {
        fields: {
          id: null,
          url: null,
          status: null,
          currentVersion: {
            fields: {
              id: null,
              status: null,
            },
          },
          publishedVersion: {
            fields: {
              id: null,
            },
          },
        },
      },
      confirmedSteps: null,
      published: null,
      lastAnalysisJob: null,
      lastPublishedJob: null,
      snippetStatus: null,
      samplePages: {
        limit: 5,
        offset: 0,
        fields: {
          status: null,
          lastGenerationJob: {
            fields: {
              status: null,
            }
          }
        }
      },
      audience: {
        fields: {
          id: null,
        }
      }
    },
  }, {
    refetchOnMount: true,
  });

  const isLogoPoolEmpty = step6Preview.editorState.isLogoPoolEmpty;
  const isSavingEditor = step6Preview.editorState.isSaving;

  if (isLoading || !campaign) {
    return (
      <div className="flex h-screen flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-9 w-40" />
        </div>
        <Skeleton className="min-h-0 flex-1 rounded-2xl" />
      </div>
    );
  }


  const isNewOrigin = campaign?.landingPageOriginType === 'new';
  // NEW-origin campaigns run two distinct journeys: phase 1 builds and
  // launches the hosted page (configure → building → edit); phase 2 — entered
  // from the details page once the page is live — creates the per-account
  // variants (audience → … → publishing). The stepper only shows the pills of
  // the phase the campaign is in, so it never promises steps that belong to
  // the other journey.
  const isVariantPhase =
    isNewOrigin &&
    (campaign?.published === true ||
      campaign?.status === 'published' ||
      campaign?.status === 'unpublished');

  const FunnelOption = <FunnelDropdownOptions options={[
    {
      label: 'Back to campaign',
      to: `${currentWorkspaceUrlPrefix}/campaigns/${id}`}
  ]} label={campaign?.name} />

  const configureStep = {
    label: 'Configure campaign',
    id: 'configure',
    path: 'configure',
    availabilityFn: availableFrom('configure'),
    showInStepper: !isVariantPhase,
    showStepper: true,
    component: Step1CampaignConfiguration,
    optionComponent: FunnelOption
  };

  // NEW-origin campaigns build & edit a Tinct-hosted page; the variant phase
  // (audience → … → publishing) is re-entered later from the dashboard's
  // "Create page variants" banner and reuses the EXISTING steps as-is — they
  // are appended below so those deep links keep resolving (FunnelRoutes only
  // mounts routes for steps present in the array).
  const newOriginOwnSteps = [
    configureStep,
    {
      label: 'Building your page',
      id: 'building',
      path: 'building',
      availabilityFn: availableFrom('building'),
      showInStepper: false,
      showStepper: false,
      component: Step2bBuilding,
      optionComponent: FunnelOption,
    },
    {
      label: 'Edit page',
      id: 'edit',
      path: 'edit',
      availabilityFn: availableFrom('edit'),
      showInStepper: !isVariantPhase,
      showStepper: true,
      component: step3Edit.component,
      optionComponent: (<div className={'inline-flex flex-row items-center gap-2'}>
        <Button variant="ghost" size="icon" asChild>
          <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/configure`}>
            <ArrowLeftIcon/>
          </Link>
        </Button>
        {FunnelOption}
      </div>),
      actions: [
        {
          id: 'upgrade',
          label: (
            <Button
              className="bg-indigo-100 text-primary hover:bg-indigo-200"
              onClick={() => openPaywall()}
            >
              <Zap />
              Upgrade
            </Button>
          ),
        },
        {
          id: 'edit-secondary',
          label: (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <EllipsisVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={step3Edit.regenerate}>
                  <RefreshCcw className="size-4" />
                  Regenerate page
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/configure`}>
                    <ArrowLeftIcon className="size-4" />
                    Back to configure
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        },
        {
          id: 'publish',
          label: (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span keeps hover events alive while the button is disabled */}
                <span className={!step3Edit.canPublish ? 'cursor-not-allowed' : undefined}>
                  <Button
                    onClick={step3Edit.openLaunchModal}
                    disabled={!step3Edit.canPublish || step3Edit.isPublishing}
                  >
                    Publish
                  </Button>
                </span>
              </TooltipTrigger>
              {step3Edit.publishDisabledReason && (
                <TooltipContent>{step3Edit.publishDisabledReason}</TooltipContent>
              )}
            </Tooltip>
          ),
        },
      ],
    },
  ];

  const existingOriginSteps = [
        configureStep,
        {
          label: 'Analyze website',
          id: 'analyze',
          path: 'analyze',
          availabilityFn: availableFrom('analyze'),
          showInStepper: false,
          showStepper: false,
          component: Step2Analyzing,
          optionComponent: FunnelOption
        },
        {
          label: 'Define Audience',
          id: 'audience',
          path: 'audience',
          availabilityFn: availableFrom('audience'),
          showInStepper: true,
          showStepper: true,
          component: step4Audience.component,
          optionComponent: (<div className={'inline-flex flex-row items-center gap-2'}>
            <Button variant="ghost" size="icon" asChild>
              {/* NEW-origin variant phase is entered from the campaign page,
                  not from configure — back mirrors where the user came from. */}
              <Link to={isNewOrigin
                ? `${currentWorkspaceUrlPrefix}/campaigns/${id}`
                : `${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/configure`}>
                <ArrowLeftIcon/>
              </Link>
            </Button>
            {FunnelOption}
          </div>),
          actions: [
            {
              id: 'save-audience',
              label: (
                <Button
                  onClick={step4Audience.saveAndContinue}
                  disabled={!step4Audience.isAudienceSelected || !step4Audience.isAudienceImportCompleted || step4Audience.isPending}
                >
                  {step4Audience.isPending ? 'Saving...' : 'Next step'}
                  {!step4Audience.isPending && <ArrowRightIcon />}
                </Button>
              ),
            },
          ],
        },
        {
          label: 'Sampling Generation',
          id: 'sampling',
          path: 'sampling',
          availabilityFn: availableFrom('sampling'),
          showInStepper: false,
          showStepper: false,
          component: Step5SampleGeneration,
          optionComponent: (<div className={'inline-flex flex-row items-center gap-2'}>
            <Button variant="ghost" size="icon" asChild>
              <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/audience`}>
                <ArrowLeftIcon />
              </Link>
            </Button>
            {FunnelOption}
          </div>),
        },
        {
          label: 'Preview & Refine',
          id: 'preview',
          path: 'preview',
          availabilityFn: availableFrom('preview'),
          showInStepper: true,
          // The "Original page / Page variants" tabs took the stepper's spot
          // for this step — they are the step's "where am I" anchor now.
          showStepper: false,
          component: step6Preview.component,
          optionComponent: (<div className={'inline-flex flex-row items-center gap-2'}>
            <Button variant="ghost" size="icon" asChild>
              <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/audience`}>
                <ArrowLeftIcon />
              </Link>
            </Button>
            {FunnelOption}
            <StudioModeTabs
              className="ml-2"
              variantsCount={step6Preview.editorState.variantsCount}
            />
          </div>),
          actions: [
            {
              id: 'upgrade',
              label: (
                <Button
                  className="bg-indigo-100 text-primary hover:bg-indigo-200"
                  onClick={() => openPaywall()}
                >
                  <Zap />
                  Upgrade
                </Button>
              ),
            },
            {
              id: 'next-step',
              label: (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span keeps the tooltip hoverable while the button is disabled */}
                      <span>
                        <Button
                          disabled={
                            isImportingBuyers ||
                            isRematchingLogos ||
                            isLogoPoolEmpty ||
                            isSavingEditor
                          }
                          onClick={async () => {
                            // The merged step covers the old editor and preview
                            // steps — confirm both until the backend merges them.
                            await addConfirmedStep({ id: id as string, confirmedStep: 'block_identification' });
                            await addConfirmedStep({ id: id as string, confirmedStep: 'validation' });
                            navigate(
                              `${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/publish`,
                              { replace: true }
                            );
                          }}
                        >
                          Continue <ArrowRightIcon />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isImportingBuyers ? (
                      <TooltipContent>
                        Importing customer logos… please wait until it finishes.
                      </TooltipContent>
                    ) : isRematchingLogos ? (
                      <TooltipContent>{REMATCHING_LOGOS_REASON}</TooltipContent>
                    ) : isLogoPoolEmpty ? (
                      <TooltipContent>
                        Add at least one customer logo, or disable the logo
                        block, to continue.
                      </TooltipContent>
                    ) : isSavingEditor ? (
                      <TooltipContent>Saving your changes…</TooltipContent>
                    ) : null}
                  </Tooltip>
                </TooltipProvider>
              ),
            },
          ],
        },
        {
          label: 'Publish',
          id: 'publish',
          path: 'publish',
          availabilityFn: availableFrom('publish'),
          showInStepper: true,
          showStepper: true,
          component: step7Publish.component,
          optionComponent: (<div className={'inline-flex flex-row items-center gap-2'}>
            <Button variant="ghost" size="icon" asChild>
              <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/preview`}>
                <ArrowLeftIcon />
              </Link>
            </Button>
            {FunnelOption}
          </div>),
          actions: [
            {
              id: 'next-step',
              label: (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* span keeps hover events alive while the button is disabled */}
                    <span className={!step7Publish.canLaunch ? 'cursor-not-allowed' : undefined}>
                      <Button
                        onClick={step7Publish.launchAndContinue}
                        disabled={!step7Publish.canLaunch || step7Publish.isPublishing}
                      >
                        Next step <ArrowRightIcon />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {step7Publish.launchBlockedReason && (
                    <TooltipContent>{step7Publish.launchBlockedReason}</TooltipContent>
                  )}
                </Tooltip>
              ),
            },
          ],
        },
        {
          label: 'Publishing',
          id: 'publishing',
          path: 'publishing',
          availabilityFn: availableFrom('publishing'),
          showInStepper: false,
          showStepper: false,
          component: Step8Publishing,
          optionComponent: FunnelOption
        },
  ];

  // [configure, analyze, audience, …] — everything after analyze is the
  // variant phase, shared verbatim with NEW-origin campaigns on re-entry.
  const newOriginSteps = [
    ...newOriginOwnSteps,
    ...existingOriginSteps.slice(2).map((step) => ({
      ...step,
      // Variant-phase pills only appear once the page is live (phase 2).
      showInStepper: (step.showInStepper ?? true) && isVariantPhase,
    })),
  ];

  // ADWORD-axis campaigns (an ads campaign — Google Ads / LinkedIn Ads: one variant per matched
  // ads campaign) run the normal per-origin funnel minus the audience step —
  // and minus sampling, which depends on an audience. Variants generate
  // automatically when the ads sync matches this campaign's landing page.
  const isAdwordAxis = campaign?.landingPageVariationAxis === 'adword';
  const perOriginSteps = isNewOrigin ? newOriginSteps : existingOriginSteps;
  const steps = !isAdwordAxis
    ? perOriginSteps
    : perOriginSteps
        .filter((step) => !['audience', 'sampling'].includes(step.id))
        .map((step) =>
          step.id === 'preview'
            ? {
                ...step,
                // The default back-arrow points at the audience step, which is
                // not mounted for this axis — go back to configure instead.
                optionComponent: (
                  <div className={'inline-flex flex-row items-center gap-2'}>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}/configure`}>
                        <ArrowLeftIcon />
                      </Link>
                    </Button>
                    {FunnelOption}
                    <StudioModeTabs
                      className="ml-2"
                      variantsCount={step6Preview.editorState.variantsCount}
                    />
                  </div>
                ),
              }
            : step,
        );

  return (
    <FunnelProvider
      steps={steps}
      state={{
        campaign: campaign,
      }}
      baseUrl={`${currentWorkspaceUrlPrefix}/funnel-campaigns/${id}`}
      exitUrl={`${currentWorkspaceUrlPrefix}/campaigns/${id}`}
    >
      <FunnelLayout contentClassName="flex-1 flex">
        <FunnelRoutes dispatcher={<CampaignCreationFunnelDispatcher />} />
      </FunnelLayout>
      {/* Rendered here (not via the step `component`) so toggling these modals
          never remounts the audience step, which caused a flicker. */}
      {step4Audience.modals}
    </FunnelProvider>
  );
};
